import { ModalDrawer } from '@egw/components'
import {
  CashEquivalentCategory,
  type EnrollmentCashEquivalentTypeRecommendationOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { type Dispatch, type FC, type SetStateAction, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from '@egw/i18n'
import { CashEquivalent } from '../cash-equivalent/cash-equivalent'
import { type RiskTolerance } from '../modal-risk-tolerance/modal-risk-tolerance'

interface CashEquivalentProps {
  isOpen: boolean
  onClose: () => void
  riskTolerance?: RiskTolerance
  handleContinueClick: (value: CashEquivalentCategory) => void
  withContainer?: boolean
  isShowCurrentValue?: boolean
  cashEquivalentData?: EnrollmentCashEquivalentTypeRecommendationOutput
  setCashEquivalentData: Dispatch<SetStateAction<CashEquivalentCategory | undefined>>
  initData?: CashEquivalentCategory
}

interface FormData {
  cashEquivalentCategory: string
}

export const ModalCashEquivalent: FC<CashEquivalentProps> = ({
  isOpen,
  onClose,
  riskTolerance,
  handleContinueClick,
  cashEquivalentData,
  setCashEquivalentData,
  initData,
}) => {
  const { t } = useTranslation('features-enrollment')

  const {
    control,
    getValues,
    formState: { isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      cashEquivalentCategory: cashEquivalentData?.current,
    },
  })

  const handleCloseModal = (): void => {
    onClose()
    reset({ cashEquivalentCategory: initData })
  }

  const updatedCashEquivalentItems = useMemo(
    () =>
      Object.values(CashEquivalentCategory)
        .filter(
          (item) =>
            !(
              item === CashEquivalentCategory.TripleTaxFree &&
              cashEquivalentData?.allowTripleTaxFreeMunicipalMutualFund === false
            ),
        )
        .map((item) => {
          const isRecommended = item === cashEquivalentData?.recommended
          return {
            value: item,
            label: t(`investments.cash-equivalent.options.${item}`),
            ...(isRecommended && { recommended: true }),
          }
        }),
    [cashEquivalentData?.allowTripleTaxFreeMunicipalMutualFund, cashEquivalentData?.recommended, t],
  )

  const onSubmit = (): void => {
    setCashEquivalentData(getValues('cashEquivalentCategory') as CashEquivalentCategory)
    handleContinueClick(getValues('cashEquivalentCategory') as CashEquivalentCategory)
  }

  return (
    <ModalDrawer isOpen={isOpen} title="" onClose={handleCloseModal} testId="cash-equivalent-modal">
      <CashEquivalent
        items={updatedCashEquivalentItems}
        riskTolerance={riskTolerance}
        onSubmit={onSubmit}
        control={control}
        isValid={isValid}
        allowTripleTaxFreeMunicipalMutualFund={cashEquivalentData?.allowTripleTaxFreeMunicipalMutualFund}
        withContainer={false}
      />
    </ModalDrawer>
  )
}
